// Brisk Certificate List — pure display rules (labels, colours, which buttons). The server decides what is allowed
// (each row comes with its `actions`); nothing here moves money or decides anything.

export const STATUS_LABELS = {
  DRAFT: "Draft",
  SUBMITTING: "Creating",
  CREATED: "Created",
  FAILED: "Not created",
  UNKNOWN: "Not known yet",
  CANCELLING: "Cancelling",
  CANCELLED: "Cancelled",
  CANCEL_UNKNOWN: "Cancel not known",
  NEEDS_REVIEW: "Needs admin",
};

export const statusLabel = (status) => STATUS_LABELS[status] || status || "-";

export const statusColor = (status) => {
  if (status === "CREATED") return "green";
  if (status === "CANCELLED" || status === "DRAFT") return "grey";
  if (status === "FAILED") return "red";
  return "#b54708"; // waiting for a Recheck or an admin
};

export const STATUS_FILTERS = [
  { _id: "all", label: "All", value: "" },
  { _id: "created", label: "Created", value: "CREATED" },
  { _id: "draft", label: "Draft", value: "DRAFT" },
  { _id: "unknown", label: "Not known yet", value: "UNKNOWN" },
  { _id: "review", label: "Needs admin", value: "NEEDS_REVIEW" },
  { _id: "failed", label: "Not created", value: "FAILED" },
  { _id: "cancelled", label: "Cancelled", value: "CANCELLED" },
];

/** The action buttons of a row, in the order they are shown (only the ones the server allowed). */
export const ACTION_BUTTONS = [
  { key: "CONTINUE", label: "Continue", hint: "Continue this draft" },
  { key: "EDIT", label: "Edit", hint: "Edit certificate" },
  { key: "RECHECK", label: "Recheck", hint: "Recheck with Brisk" },
  { key: "RESOLVE", label: "Resolve", hint: "Resolve this certificate" },
  { key: "CANCEL", label: "Cancel", hint: "Cancel certificate" },
  { key: "HISTORY", label: "History", hint: "View history" },
];
export const rowButtons = (row) => ACTION_BUTTONS.filter((b) => (row.actions || []).includes(b.key));

/** What the Company Wallet did for this certificate, in a few words for the History dialog. */
export const walletLine = (row) => {
  if (!row) return "";
  if (row.charge && row.charge.state === "PREPAID_BY_POLICY") {
    const paid = `Paid with policy ${row.source && row.source.policyNo ? row.source.policyNo : ""}`.trim();
    return row.refund && row.refund.state === "DONE" && row.refund.amount ? `${paid} · ₹${row.refund.amount} refunded` : paid;
  }
  if (row.charge && row.charge.state === "DEBITED") {
    if (row.refund && row.refund.state === "DONE") return `₹${row.charge.amount} charged and refunded`;
    return `₹${row.charge.amount} charged to the Brisk wallet`;
  }
  return "Nothing charged";
};

/** One step of a certificate's History in plain words: a short title, its colour and picture. The full sentence stays under it. */
const STEP_BY_STATUS = {
  SUBMITTING: { title: "Sent to Brisk", tone: "navy", icon: "sent" },
  CREATED: { title: "Certificate created", tone: "green", icon: "done" },
  FAILED: { title: "Not created", tone: "red", icon: "failed" },
  UNKNOWN: { title: "No clear answer from Brisk", tone: "amber", icon: "warning" },
  CANCELLING: { title: "Cancel requested", tone: "amber", icon: "cancel" },
  CANCELLED: { title: "Certificate cancelled", tone: "grey", icon: "cancel" },
  CANCEL_UNKNOWN: { title: "No clear answer to the cancel", tone: "amber", icon: "warning" },
  NEEDS_REVIEW: { title: "Needs an admin", tone: "amber", icon: "warning" },
  DRAFT: { title: "Saved as a draft", tone: "grey", icon: "note" },
};
const STEP_BY_KIND = {
  money: { title: "Wallet", tone: "navy", icon: "money" },
  amount: { title: "Payment", tone: "navy", icon: "money" },
  fields: { title: "Details changed", tone: "navy", icon: "edit" },
  note: { title: "Note", tone: "grey", icon: "note" },
};
export const historyStep = (entry = {}) => {
  // A cancel Brisk refused goes back to the status it had, so the status alone would read as "created" again.
  if (entry.from === "CANCELLING" && !["CANCELLED", "CANCEL_UNKNOWN"].includes(entry.to)) return { title: "Not cancelled", tone: "amber", icon: "warning" };
  if (entry.from === "SUBMITTING" && entry.to === "DRAFT") return { title: "Not started", tone: "amber", icon: "warning" };
  if (entry.kind === "note" && /^Recheck\b/.test(String(entry.text))) return { title: "Recheck", tone: "navy", icon: "recheck" };
  return (entry.kind === "status" && STEP_BY_STATUS[entry.to]) || STEP_BY_KIND[entry.kind] || { title: "Update", tone: "grey", icon: "note" };
};

/** "EngineNo" -> "Engine No", for the changed-details list. */
const fieldLabel = (field) => {
  const spaced = String(field).replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The server writes a change as "Changed from the policy: EngineNo A -> B; ChassisNo C -> D". Read as a list, so each
 * change gets its own line. Null when the sentence is not that shape: the sentence is then shown as it is.
 */
export const parseFieldChanges = (text) => {
  const whole = /^(Changed [^:]+): (.+)$/.exec(String(text || ""));
  if (!whole) return null;
  const changes = whole[2].split("; ").map((part) => {
    const one = /^(\S+) (.*) -> (.*)$/.exec(part);
    return one && { field: one[1], label: fieldLabel(one[1]), from: one[2], to: one[3] };
  });
  return changes.every(Boolean) ? { lead: whole[1], changes } : null;
};

/** What each call to Brisk was for, in plain words. The exact call name stays in the details, for support. */
const BRISK_CALL_LABELS = {
  ValidateEngineno: "Checked the engine number",
  ValidateChassisno: "Checked the chassis number",
  DealerWalletBalance: "Checked the Brisk wallet balance",
  GenerateCertificateAsync: "Asked Brisk to create the certificate",
  IssuecertificateAsync: "Asked Brisk to create the certificate",
  CancelCertificate: "Asked Brisk to cancel the certificate",
  UpdateCertificate: "Asked Brisk to change the certificate",
  Certificatedetails: "Read the certificate's details from Brisk",
  ViewCertificate: "Asked Brisk for the certificate PDF",
  CertificatePdf: "Recheck. Asked Brisk's address for the PDF",
  WalletTopup: "Started a Brisk wallet top up",
  CashfreeOrderStatus: "Checked a top up payment",
};
export const briskCallLabel = (endpoint) => BRISK_CALL_LABELS[endpoint] || endpoint || "Call to Brisk";

/** Did Brisk answer? { label, ok } in plain words. */
export const briskCallOutcome = (call = {}) => {
  if (call.sent === "NOT_SENT") return { label: "Not sent", ok: false };
  if (call.sent !== "ANSWERED") return { label: "No answer", ok: false };
  if (call.endpoint === "CertificatePdf" && call.httpStatus === 404) return { label: "Not there yet", ok: false };
  return call.httpStatus >= 200 && call.httpStatus < 300 ? { label: "Answered", ok: true } : { label: "Refused", ok: false };
};

/** What a cancel will give back, said before it is pressed. The server decides; this only tells. */
export const cancelRefundLine = (row) => {
  if (!row) return "";
  if (!row.charge || row.charge.state !== "PREPAID_BY_POLICY") return `₹${row.charge && row.charge.amount} goes back to the Brisk wallet when Brisk cancels it.`;
  const policyNo = (row.source && row.source.policyNo) || "";
  if (row.apiEnv !== "LIVE") return "This is a TEST certificate, so nothing is refunded.";
  if (!row.source || !row.source.briskCharged) return `Nothing was taken from the Brisk wallet for policy ${policyNo}, so nothing is refunded.`;
  return `₹${row.source.briskCharged} taken for policy ${policyNo} goes back to the Brisk wallet when Brisk cancels it.`;
};

/** Only http(s) links are ever opened (a javascript: link from anywhere is not). */
export const isSafeLink = (url) => {
  try {
    const u = new URL(String(url));
    return u.protocol === "https:" || u.protocol === "http:";
  } catch (e) {
    return false;
  }
};
